import React from "react";
import { FiGlobe, FiPhone, FiMail, FiUser, FiMapPin, FiCalendar } from "react-icons/fi";
import TextBoxHTML from "newui/components/TextBoxHTML";
import type { SupplierPerformanceDetail } from "../../types/supplier-performance.types";

const PROFILE_TYPE_NAMES: Record<number, string> = {
	1: "Client",
	2: "Head Contractor",
	3: "Subcontractor",
	4: "Consultant",
};

const SIZE_TYPE_NAMES: Record<number, string> = {
	1: "Micro (1-5)",
	2: "Small (6-20)",
	3: "Medium (21-100)",
	4: "Large (101-500)",
	5: "Enterprise (500+)",
};

interface CompanyOverviewTabProps {
	supplier: SupplierPerformanceDetail;
}

interface DetailRowProps {
	icon: React.ReactNode;
	label: string;
	value: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon, label, value }) => (
	<div className="flex items-start gap-3 py-2">
		<div className="mt-0.5 text-gray-400">{icon}</div>
		<div>
			<div className="text-xs font-medium text-gray-500">{label}</div>
			<div className="text-sm text-gray-900">{value}</div>
		</div>
	</div>
);

export const CompanyOverviewTab: React.FC<CompanyOverviewTabProps> = ({
	supplier,
}) => {
	const profileTypeName = PROFILE_TYPE_NAMES[supplier.profile_type_id] ?? "Unknown";
	const sizeName = supplier.size_type_id
		? SIZE_TYPE_NAMES[supplier.size_type_id] ?? null
		: null;

	const locationParts = [supplier.city, supplier.state, supplier.postcode].filter(Boolean);
	const locationString = supplier.location || locationParts.join(", ") || null;

	return (
		<div className="space-y-6">
			<div className="rounded-lg border border-gray-200 bg-white p-6">
				<h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">
					Contact & Details
				</h3>
				<div className="grid grid-cols-1 gap-x-8 gap-y-1 md:grid-cols-2">
					<DetailRow
						icon={<FiUser className="h-4 w-4" />}
						label="Company Type"
						value={profileTypeName}
					/>
					{supplier.contact_name && (
						<DetailRow
							icon={<FiUser className="h-4 w-4" />}
							label="Primary Contact"
							value={supplier.contact_name}
						/>
					)}
					{supplier.email && (
						<DetailRow
							icon={<FiMail className="h-4 w-4" />}
							label="Email"
							value={
								<a
									href={`mailto:${supplier.email}`}
									className="text-blue-600 hover:text-blue-800"
								>
									{supplier.email}
								</a>
							}
						/>
					)}
					{supplier.phone && (
						<DetailRow
							icon={<FiPhone className="h-4 w-4" />}
							label="Phone"
							value={supplier.phone}
						/>
					)}
					{supplier.website && (
						<DetailRow
							icon={<FiGlobe className="h-4 w-4" />}
							label="Website"
							value={
								<a
									href={supplier.website.startsWith("http") ? supplier.website : `https://${supplier.website}`}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 hover:text-blue-800"
								>
									{supplier.website}
								</a>
							}
						/>
					)}
					{locationString && (
						<DetailRow
							icon={<FiMapPin className="h-4 w-4" />}
							label="Location"
							value={locationString}
						/>
					)}
					{supplier.abn && (
						<DetailRow
							icon={<FiCalendar className="h-4 w-4" />}
							label="ABN"
							value={supplier.abn}
						/>
					)}
					{supplier.years_trading != null && (
						<DetailRow
							icon={<FiCalendar className="h-4 w-4" />}
							label="Years Trading"
							value={`${supplier.years_trading} years`}
						/>
					)}
					{sizeName && (
						<DetailRow
							icon={<FiUser className="h-4 w-4" />}
							label="Company Size"
							value={sizeName}
						/>
					)}
				</div>
			</div>

			{supplier.description && (
				<div className="rounded-lg border border-gray-200 bg-white p-6">
					<h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">
						About
					</h3>
					<div className="prose prose-sm max-w-none text-gray-700">
						<TextBoxHTML html={supplier.description} />
					</div>
				</div>
			)}
		</div>
	);
};
